<template>
  <div class="board audience-board" :class="componentClass">
    <div class="level audience-top-nav">
      <div class="level-left">
        <audience-filter @audience-filter-toggle="onFilterToggle" />
      </div>
      <div class="level-right">
        <div class="tags has-addons">
          <span class="tag">
            <i class="material-icons">&#xE192;</i>
            <span>{{ $t('audience.filter.real_time') }}</span>
          </span>
          <span class="tag">
            <i class="material-icons">person</i>
            <span>{{ userCount }}</span>
            <div class="users-limit">
              <i
                class="material-icons animated bounceIn"
                v-if="userCount >= usersLimit"
                >warning</i
              >
              <div class="tooltip tooltip-right" role="tooltip">
                <div class="tooltip-inner">
                  {{
                    $t('audience.filter.users_limit', { amount: usersLimit })
                  }}
                </div>
                <div class="tooltip-arrow"></div>
              </div>
            </div>
          </span>
        </div>
        <audience-select-view :view="view" @change="onViewChange" />
        <language-switcher />
      </div>
    </div>

    <div class="tile is-ancestor">
      <!-- left side -->
      <div id="summary-tile" class="tile is-parent" :class="summaryClass">
        <div class="tile is-child" id="map-tile">
          <audience-map />
        </div>
        <div class="tile is-child" id="moment-tile">
          <moment-tracker :cols="view === 'summary' ? 2 : 3" />
        </div>
      </div>

      <!-- right side -->
      <div
        id="detail-tile"
        class="tile is-parent is-7 is-dark"
        v-if="view === 'detail'"
      >
        <div class="tile is-6 is-child">
          <active-segments />
        </div>
        <div class="tile is-6 is-vertical has-margin-left">
          <div class="tile is-child">
            <transport-modes />
          </div>
          <div class="tile is-child">
            <driving-behavior />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AudienceFilter from './filter/AudienceFilter';
import AudienceSelectView from './filter/AudienceSelectView';
import LanguageSwitcher from 'site/nav/LanguageSwitcher';
import AudienceMap from './map/AudienceMap';
import MomentTracker from './moments/MomentTracker';
import ActiveSegments from './segments/ActiveSegments';
import TransportModes from './transport/TransportModes';
import DrivingBehavior from './transport/DrivingBehavior';
import { mapState } from 'vuex';
import { configs } from 'config';

export default {
  components: {
    LanguageSwitcher,
    AudienceSelectView,
    AudienceFilter,
    AudienceMap,
    MomentTracker,
    ActiveSegments,
    TransportModes,
    DrivingBehavior,
  },

  data() {
    return {
      isFilterActive: false,
      usersLimit: configs().USERS_LIMIT,
    };
  },

  computed: {
    ...mapState('audience', ['users']),

    userCount() {
      return this.users.length;
    },

    componentClass() {
      return {
        'has-active-filter': this.isFilterActive,
        [this.view + '-view']: true,
      };
    },

    summaryClass() {
      return this.view === 'summary'
        ? 'is-12 is-horizontal'
        : 'is-5 is-vertical';
    },

    view() {
      return this.$store.state.audience.activeBoardView;
    },
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (vm.users.length === 0) {
        vm.$store.dispatch('audience/loadUsers');
        vm.onViewChange(vm.$store.state.route.query.view || 'summary');
      }
    });
  },

  methods: {
    onFilterToggle(newValue) {
      this.isFilterActive = newValue;
    },

    onViewChange(view) {
      this.$store.commit('audience/setActiveBoardView', view);
      window.dispatchEvent(new Event('resize'));
    },
  },

  watch: {
    $route(to, from) {
      if (this.users.length === 0) {
        this.$store.dispatch('audience/loadUsers');
        this.onViewChange(this.$store.state.route.query.view || 'summary');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';
@import '~scss/utilities/mixins.scss';

.audience-board {
  background-color: #f6f6f6;

  // this prevents a momentary resize as scrollbars appear, then disappear, which causes mapbox not resize and leave a black edge
  overflow: hidden;

  &.has-active-filter {
    &:before {
      @include overlay();
      content: ' ';
      top: 40px;
      background-color: rgba($black, 0.4);
      z-index: 10;
    }
  }
}

.tile {
  &.is-ancestor {
    min-height: calc(100vh - 104px);
    max-height: calc(100vh - 104px);
    margin: 0;
  }

  &.is-child {
    flex: 1;
  }

  &.is-dark {
    // background-color: #323435;
    background-color: rgba($black, 0.12);
  }

  &.has-margin-left {
    margin-left: 0.5rem;
  }

  &.has-margin-top {
    margin-top: 0.5rem;
  }

  &.is-parent.is-horizontal > .tile:not(:first-child) {
    margin-left: 0.5rem !important;
  }
}

.audience-top-nav {
  margin-bottom: 0;
  background-color: $white;
  box-shadow: 0 2px 12px 0 rgba($black, 0.06);
  padding-left: 3rem;
  padding-right: 3rem;

  &.is-dark {
    background-color: #0f0f0f;
    background: linear-gradient(to bottom, #1e1e1e 0%, #0f0f0f 100%);
    padding: 0;
  }

  .tags,
  .tag {
    margin-bottom: 0;
  }

  .tags {
    margin-right: 1.5rem;
  }

  .tag {
    background-color: #66bb6a;
    border-radius: 4px;
    color: $white;
    font-size: 0.6875rem;
    font-weight: $weight-medium;
    letter-spacing: 2px;
    text-transform: uppercase;
    cursor: default;

    &:last-child {
      background-color: darken(#66bb6a, 12%);
    }

    .users-limit {
      i {
        margin-left: 0.25rem;
        color: $yellow;
        cursor: pointer;
      }
    }

    i {
      font-size: 0.6875rem;

      & + span {
        margin-left: 0.25rem;
      }
    }
  }

  .users-limit {
    position: relative;
    top: 1px;

    .tooltip {
      margin-left: 0.5rem;

      .tooltip-inner {
        padding: 0.5rem;
      }
    }

    &:hover {
      .tooltip {
        opacity: 1;
      }
    }
  }
}
</style>

<style lang="scss">
@import '~scss/utilities/initial-variables.scss';
@import '~scss/utilities/derived-variables.scss';

.audience-board {
  .card {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .card-header {
    align-items: center;
    padding: 0.796875rem 1rem;

    .level {
      flex: 1;
    }
  }

  .card-header-title {
    margin-top: 0;
  }

  .card-content {
    display: flex;
    flex-grow: 1;
  }
}
</style>
